import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AuthPageLayout.module.css';

type AuthPageLayoutProps = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 7.2C4 6.08 4.9 5.2 6 5.2h12c1.1 0 2 .88 2 1.98v9.6c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V7.2Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="m5 7 7 5 7-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 11V8.5a4 4 0 1 1 8 0V11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const EyeIcon = ({ hidden }: { hidden?: boolean }) => (
  hidden ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M10.6 10.6A2.5 2.5 0 0 0 12 16.5a2.5 2.5 0 0 0 1.4-5.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.7 6.8C8.5 5.6 10.2 5 12 5c5 0 9 4 10.3 7-0.5 1.2-1.4 2.5-2.6 3.7M9.9 14.1C8.8 13.4 7.8 12.3 7 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M2.5 12C3.8 8 7.8 4 12 4s8.2 4 9.5 8c-1.3 4-5.3 8-9.5 8S3.8 16 2.5 12Z" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
);

const SparkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.8 15.6 6 18.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3 5 6v6c0 4.2 3 7.8 7 9 4-1.2 7-4.8 7-9V6l-7-3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const ChartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 19V9M12 19V5M19 19v-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const AuthPageLayout: React.FC<AuthPageLayoutProps> = ({
  title,
  subtitle,
  children,
  footer,
}) => {
  const { t } = useTranslation(['auth', 'header']);

  return (
    <div className={styles.page}>
      <aside className={styles.brandPanel} aria-hidden="true">
        <div className={styles.brandPanelInner}>
          <div className={styles.brandHeader}>
            <div className={styles.brandMark}>{t('brandInitials', { ns: 'header' })}</div>
            <div className={styles.brandHeaderText}>
              <h1 className={styles.brandName}>{t('brandName', { ns: 'header' })}</h1>
              <p className={styles.brandTagline}>{t('brandSubtitle', { ns: 'header' })}</p>
            </div>
          </div>
          <ul className={styles.brandHighlights}>
            <li className={styles.brandHighlight}>
              <span className={styles.brandHighlightIcon}><SparkIcon /></span>
              {t('brandHighlightAi', { ns: 'auth' })}
            </li>
            <li className={styles.brandHighlight}>
              <span className={styles.brandHighlightIcon}><ChartIcon /></span>
              {t('brandHighlightInsights', { ns: 'auth' })}
            </li>
            <li className={styles.brandHighlight}>
              <span className={styles.brandHighlightIcon}><ShieldIcon /></span>
              {t('brandHighlightSecure', { ns: 'auth' })}
            </li>
          </ul>
        </div>
      </aside>

      <main className={styles.formPanel}>
        <div className={styles.formShell}>
          <div className={styles.mobileBrand}>
            <div className={styles.mobileBrandMark}>{t('brandInitials', { ns: 'header' })}</div>
            <div className={styles.mobileBrandText}>
              <span className={styles.mobileBrandName}>{t('brandName', { ns: 'header' })}</span>
              <span className={styles.mobileBrandSub}>{t('brandSubtitle', { ns: 'header' })}</span>
            </div>
          </div>

          <div className={styles.formCard}>
            <header className={styles.formHeader}>
              <h2 className={styles.formTitle}>{title}</h2>
              {subtitle ? <p className={styles.formSubtitle}>{subtitle}</p> : null}
            </header>
            {children}
          </div>

          {footer}
        </div>
      </main>
    </div>
  );
};

type AuthFieldProps = {
  label: string;
  htmlFor: string;
  icon?: 'mail' | 'lock';
  children: React.ReactNode;
};

export const AuthField: React.FC<AuthFieldProps> = ({ label, htmlFor, icon, children }) => (
  <div className={styles.field}>
    <label className={styles.fieldLabel} htmlFor={htmlFor}>
      {label}
    </label>
    <div className={styles.inputWrap}>
      {icon ? (
        <span className={styles.inputIcon}>
          {icon === 'mail' ? <MailIcon /> : <LockIcon />}
        </span>
      ) : null}
      {children}
    </div>
  </div>
);

type AuthInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  withToggle?: boolean;
  revealed?: boolean;
  onToggleReveal?: () => void;
  toggleLabel?: string;
  variant?: 'default' | 'code';
};

export const AuthInput = React.forwardRef<HTMLInputElement, AuthInputProps>(function AuthInput(
  {
    withToggle,
    revealed,
    onToggleReveal,
    toggleLabel,
    variant = 'default',
    className,
    ...props
  },
  ref,
) {
  return (
    <>
      <input
        ref={ref}
        className={[
          styles.input,
          variant === 'code' ? styles.inputCode : '',
          withToggle ? styles.inputWithToggle : '',
          className,
        ].filter(Boolean).join(' ')}
        {...props}
      />
      {withToggle ? (
        <button
          type="button"
          className={styles.togglePassword}
          onClick={onToggleReveal}
          aria-label={toggleLabel}
        >
          <EyeIcon hidden={!revealed} />
        </button>
      ) : null}
    </>
  );
});

type AuthAlertProps = {
  variant: 'error' | 'success';
  children: React.ReactNode;
};

export const AuthAlert: React.FC<AuthAlertProps> = ({ variant, children }) => (
  <div
    className={[styles.alert, variant === 'error' ? styles.alertError : styles.alertSuccess].join(' ')}
    role={variant === 'error' ? 'alert' : 'status'}
  >
    {children}
  </div>
);

type AuthSubmitButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  loadingLabel?: string;
};

export const AuthSubmitButton: React.FC<AuthSubmitButtonProps> = ({
  loading,
  loadingLabel,
  children,
  disabled,
  ...props
}) => (
  <button className={styles.submitButton} type="submit" disabled={disabled || loading} {...props}>
    <span className={styles.submitButtonContent}>
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      <span>{loading && loadingLabel ? loadingLabel : children}</span>
    </span>
  </button>
);

export const authStyles = styles;

export default AuthPageLayout;
